import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import {
  actionSnapshotStateDisplayLabel,
  correctiveActionStateDisplayLabel,
  escalationStateDisplayLabel,
  measurementStateDisplayLabel,
  ownershipStateDisplayLabel,
  reviewOutcomeDisplayLabel,
} from './workspaceUtils';
import {
  SAFETY_REPORT_EMPTY_EVIDENCE_POINTS,
} from './safetyReportUxCopy';

type Fact = { label: string; value: string };
type CountRow = { count: number; [key: string]: unknown };

function RollupSection({
  title,
  summary,
  rows,
  rowLabel,
}: {
  title: string;
  summary: string;
  rows: CountRow[];
  rowLabel: (row: CountRow) => string;
}) {
  if (!summary && rows.length === 0) return null;
  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.04, textTransform: 'uppercase' }}>
        {title}
      </Typography>
      {summary ? (
        <Typography variant="body2" sx={{ mt: 0.45, lineHeight: 1.5 }}>
          {summary}
        </Typography>
      ) : null}
      {rows.length > 0 ? (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
          {rows.map((row) => (
            <Chip
              key={`${title}-${rowLabel(row)}-${row.count}`}
              size="small"
              variant="outlined"
              label={`${rowLabel(row)} · ${row.count}`}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

type ProofSummaryAdvancedSectionsProps = {
  customerEvidencePoints: string[];
  customerReviewStatusSummary: string;
  customerReviewChecklist: string[];
  customerReviewOutcomeSummary: string;
  customerReviewTrendSummary?: string;
  customerReviewBoundarySummary?: string;
  customerReviewOutcomeRows: CountRow[];
  customerOwnershipSummary: string;
  customerOwnershipDetailFacts: Fact[];
  customerOwnershipRows: CountRow[];
  customerEscalationSummary: string;
  customerEscalationRows: CountRow[];
  customerCorrectiveActionSummary: string;
  customerCorrectiveActionRows: CountRow[];
  customerMeasurementSummary: string;
  customerMeasurementBoundarySummary?: string;
  customerMeasurementRows: CountRow[];
  customerActionSnapshotSummary: string;
  customerActionSnapshotRows: CountRow[];
  customerFollowupFacts: Fact[];
  customerUnprovenSummary: string;
};

export default function ProofSummaryAdvancedSections({
  customerEvidencePoints,
  customerReviewStatusSummary,
  customerReviewChecklist,
  customerReviewOutcomeSummary,
  customerReviewTrendSummary,
  customerReviewBoundarySummary,
  customerReviewOutcomeRows,
  customerOwnershipSummary,
  customerOwnershipDetailFacts,
  customerOwnershipRows,
  customerEscalationSummary,
  customerEscalationRows,
  customerCorrectiveActionSummary,
  customerCorrectiveActionRows,
  customerMeasurementSummary,
  customerMeasurementBoundarySummary,
  customerMeasurementRows,
  customerActionSnapshotSummary,
  customerActionSnapshotRows,
  customerFollowupFacts,
  customerUnprovenSummary,
}: ProofSummaryAdvancedSectionsProps) {
  return (
    <Stack spacing={1.25} data-testid="safety-report-advanced-sections">
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          {displaySafetyManagerLabel('evidence_package')}
        </Typography>
        {customerEvidencePoints.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.5 }}>
            {SAFETY_REPORT_EMPTY_EVIDENCE_POINTS}
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
            {customerEvidencePoints.map((item) => (
              <Typography key={item} component="li" variant="body2" sx={{ lineHeight: 1.55 }}>
                {item}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          Evidence Review
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.45 }}>
          {customerReviewStatusSummary}
        </Typography>
        {customerReviewChecklist.length > 0 ? (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
            {customerReviewChecklist.map((item) => (
              <Typography key={item} component="li" variant="body2" sx={{ lineHeight: 1.45 }}>
                {item}
              </Typography>
            ))}
          </Box>
        ) : null}
        <Box
          sx={{
            mt: 0.75,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 0.75,
          }}
        >
          <RollupSection
            title="Evidence review in this report scope"
            summary={
              customerReviewTrendSummary
                ? `${customerReviewOutcomeSummary} ${customerReviewTrendSummary}${customerReviewBoundarySummary ? ` ${customerReviewBoundarySummary}` : ''}`
                : customerReviewBoundarySummary
                  ? `${customerReviewOutcomeSummary} ${customerReviewBoundarySummary}`
                  : customerReviewOutcomeSummary
            }
            rows={customerReviewOutcomeRows}
            rowLabel={(row) => reviewOutcomeDisplayLabel(row.review_outcome)}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, letterSpacing: 0.04, textTransform: 'uppercase' }}>
              Ownership
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 0.75,
              }}
            >
              {customerOwnershipDetailFacts.map((fact) => (
                <Paper key={fact.label} variant="outlined" sx={{ p: 0.85, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.02 }}>
                    {fact.label}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mt: 0.25, fontWeight: 700, lineHeight: 1.4 }}>
                    {fact.value}
                  </Typography>
                </Paper>
              ))}
            </Box>
            {customerOwnershipRows.length > 0 && customerOwnershipSummary ? (
              <RollupSection
                title=""
                summary={customerOwnershipSummary}
                rows={customerOwnershipRows}
                rowLabel={(row) => ownershipStateDisplayLabel(row.ownership_state)}
              />
            ) : null}
          </Box>
          <RollupSection
            title={displaySafetyManagerLabel('escalation')}
            summary={customerEscalationSummary}
            rows={customerEscalationRows}
            rowLabel={(row) => escalationStateDisplayLabel(row.escalation_state)}
          />
          <RollupSection
            title={displaySafetyManagerLabel('corrective_action')}
            summary={customerCorrectiveActionSummary}
            rows={customerCorrectiveActionRows}
            rowLabel={(row) => correctiveActionStateDisplayLabel(row.corrective_action_state)}
          />
          <RollupSection
            title={displaySafetyManagerLabel('followup_measurement')}
            summary={
              customerMeasurementBoundarySummary
                ? `${customerMeasurementSummary} ${customerMeasurementBoundarySummary}`
                : customerMeasurementSummary
            }
            rows={customerMeasurementRows}
            rowLabel={(row) => measurementStateDisplayLabel(row.measurement_state)}
          />
          <RollupSection
            title="Action workflow"
            summary={customerActionSnapshotSummary}
            rows={customerActionSnapshotRows}
            rowLabel={(row) => actionSnapshotStateDisplayLabel(row.action_state)}
          />
        </Box>
      </Box>
      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          Action Ownership and Follow-up
        </Typography>
        {customerFollowupFacts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45, lineHeight: 1.5 }}>
            No follow-up action has been recorded for this report scope yet.
          </Typography>
        ) : (
          <Box
            sx={{
              mt: 0.75,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' },
              gap: 1,
            }}
          >
            {customerFollowupFacts.map((fact) => (
              <Box key={`operational-outcome-${fact.label}`} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">{fact.label}</Typography>
                <Typography variant="subtitle2" sx={{ mt: 0.35, fontWeight: 700, lineHeight: 1.45 }}>{fact.value}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: -0.01 }}>
          Residual Limits
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.45, lineHeight: 1.5 }}>
          {customerUnprovenSummary}
        </Typography>
      </Box>
    </Stack>
  );
}
